import React from "react";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import { Gantt, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import StageModal from "../StageModal";
import TaskEditModal from "../TaskEditModal";
import "./ProjectGanttTab.css";

const ProjectGanttTab = ({
  // Gantt Data
  finalGanttTasks,
  stages,

  // View State
  viewMode,
  columnWidth,

  // UI State
  showClosed,
  setShowClosed,
  sortBy,
  setSortBy,
  showFilterPanel,
  setShowFilterPanel,
  filterStatus,
  setFilterStatus,
  filterPriority,
  setFilterPriority,
  filterAssignee,
  setFilterAssignee,
  searchQuery,
  setSearchQuery,
  timePeriod,
  handleTimePeriodChange,

  // Team Members for Filter
  teamMemberDetails,

  // Handlers
  handleResetFilters,
  scrollToToday,
  handleAutoFit,
  exportPNG,
  exportPDF,
  exportExcel,
  handleTaskChange,

  // Stage Modal
  showStageModal,
  setShowStageModal,
  editingStage,
  setEditingStage,
  projectId,
  loadProjectStages,

  // Task Edit Modal
  showTaskEditModal,
  setShowTaskEditModal,
  editingTaskId,
  setEditingTaskId,
  loadProjectTasksByStages,

  // Collapse State
  collapsedStages,
  setCollapsedStages,
}) => {
  return (
    <div className="pgt-gantt-wrapper">
      {/* Toolbar Section */}
      <div className="pgt-toolbar">
        {/* Left Side Controls */}
        <div className="pgt-toolbar-left">
          <div className="pgt-toggle-group">
            <button
              className={`pgt-toggle-btn ${!showClosed ? "pgt-active" : ""}`}
              onClick={() => setShowClosed(false)}
            >
              <i className="fas fa-play-circle"></i>
              Active
            </button>
            <button
              className={`pgt-toggle-btn ${showClosed ? "pgt-active" : ""}`}
              onClick={() => setShowClosed(true)}
            >
              <i className="fas fa-archive"></i>
              Closed
            </button>
          </div>

          <div className="pgt-filter-group">
            <select
              className="pgt-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="stageStart">Stage Start Date</option>
              <option value="stageEnd">Stage End Date</option>
              <option value="taskStart">Task Start Date</option>
              <option value="taskEnd">Task End Date</option>
              <option value="status">Status</option>
              <option value="priority">Priority</option>
              <option value="assignee">Assignee</option>
              <option value="duration">Duration</option>
            </select>

            <button
              className={`pgt-filter-btn ${showFilterPanel ? "pgt-active" : ""}`}
              onClick={() => setShowFilterPanel(!showFilterPanel)}
            >
              <i className="fas fa-filter"></i>
              Filter
            </button>

            <button
              className="pgt-reset-btn"
              onClick={handleResetFilters}
            >
              <i className="fas fa-undo-alt"></i>
              Reset
            </button>
          </div>
        </div>

        {/* Right Side Controls */}
        <div className="pgt-toolbar-right">
          <button className="pgt-icon-btn" onClick={scrollToToday}>
            <i className="fas fa-calendar-day"></i>
            Today
          </button>

          <select
            className="pgt-select"
            value={timePeriod}
            onChange={(e) => handleTimePeriodChange(e.target.value)}
          >
            <option value="Day">Day View</option>
            <option value="Week">Week View</option>
            <option value="Month">Month View</option>
            <option value="Quarter">Quarter View</option>
            <option value="Year">Year View</option>
            <option value="Flexible">Flexible</option>
          </select>

          <button className="pgt-icon-btn" onClick={handleAutoFit}>
            <i className="fas fa-expand-alt"></i>
            Auto Fit
          </button>

          <div className="pgt-export-group">
            <button className="pgt-export-btn pgt-png" onClick={exportPNG}>
              <i className="fas fa-camera"></i>
              PNG
            </button>
            <button className="pgt-export-btn pgt-pdf" onClick={exportPDF}>
              <i className="fas fa-file-pdf"></i>
              PDF
            </button>
            <button className="pgt-export-btn pgt-excel" onClick={exportExcel}>
              <i className="fas fa-file-excel"></i>
              Excel
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="pgt-search-wrapper">
          <i className="fas fa-search pgt-search-icon"></i>
          <input
            type="text"
            placeholder="Search stage or task..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pgt-search-input"
          />
        </div>
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="pgt-filter-panel">
          <div className="pgt-filter-grid">
            <div className="pgt-filter-section">
              <label className="pgt-filter-label">Status</label>
              <select
                multiple
                className="pgt-multiselect"
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
                size={4}
              >
                <option>Not Started</option>
                <option>In Progress</option>
                <option>On Hold</option>
                <option>Completed</option>
                <option>Cancelled</option>
              </select>
            </div>

            <div className="pgt-filter-section">
              <label className="pgt-filter-label">Priority</label>
              <select
                multiple
                className="pgt-multiselect"
                value={filterPriority}
                onChange={(e) =>
                  setFilterPriority(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
                size={3}
              >
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
            </div>

            <div className="pgt-filter-section">
              <label className="pgt-filter-label">Assignee</label>
              <select
                multiple
                className="pgt-multiselect"
                value={filterAssignee}
                onChange={(e) =>
                  setFilterAssignee(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
                size={4}
              >
                {teamMemberDetails.map((user) => (
                  <option key={user.userId} value={String(user.userId)}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Gantt Chart Container */}
      <div id="pgt-gantt-container" className="pgt-gantt-container">
        <div className="pgt-gantt-card">
          {finalGanttTasks.length === 0 ? (
            <div className="pgt-empty-state">
              <i className="fas fa-chart-line"></i>
              <h4>No Tasks Available</h4>
              <p>There are no valid tasks to display in the Gantt view.</p>
              <button 
                className="pgt-empty-btn"
                onClick={() => loadProjectTasksByStages()}
              >
                Refresh Data
              </button>
            </div>
          ) : (
            <div className="pgt-gantt-chart">
              <Gantt
                tasks={finalGanttTasks}
                viewMode={viewMode}
                columnWidth={
                  viewMode === ViewMode.Month
                    ? 300
                    : viewMode === ViewMode.Year
                      ? 350
                      : viewMode === ViewMode.Week
                      ? 100
                      : 65
                }
                listCellWidth="250px"
                onDateChange={handleTaskChange}
                onExpanderClick={(task) => {
                  if (task.type !== "project") return;
                  const stageId = task.id.replace("stage-", "");
                  setCollapsedStages((prev) => ({
                    ...prev,
                    [stageId]: !prev[stageId],
                  }));
                }}
                onDoubleClick={(task) => {
                  if (!task) return;

                  if (task.type === "project") {
                    const stageId = task.id.replace("stage-", "");
                    const stageData = stages.find(
                      (s) => String(s.stageId) === String(stageId),
                    );
                    if (stageData) {
                      setEditingStage(stageData);
                      setShowStageModal(true);
                    }
                    return;
                  }

                  setEditingTaskId(task.id);
                  setShowTaskEditModal(true);
                }}
                todayColor="rgba(239, 68, 68, 0.15)"
                rtl={false}
                barCornerRadius={6}
                barFill={80}
                barBackgroundColor="#f3f4f6"
                fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <StageModal
        show={showStageModal}
        onHide={() => {
          setShowStageModal(false);
          setEditingStage(null);
        }}
        stage={editingStage}
        projectId={projectId}
        onStageSaved={() => {
          loadProjectStages();
        }}
      />

      <TaskEditModal
        show={showTaskEditModal}
        onHide={() => {
          setShowTaskEditModal(false);
          setEditingTaskId(null);
        }}
        taskId={editingTaskId}
        onTaskUpdated={() => {
          loadProjectTasksByStages();
        }}
      />
    </div>
  );
};

export default ProjectGanttTab;

// import React from "react";
// import { Row, Col, Button, Form, Card } from "react-bootstrap";
// import { Gantt, ViewMode } from "gantt-task-react";
// import "gantt-task-react/dist/index.css";
// import StageModal from "../StageModal";
// import TaskEditModal from "../TaskEditModal";

// const ProjectGanttTab = ({
//   // Gantt Data
//   finalGanttTasks,
//   stages,

//   // View State
//   viewMode,
//   columnWidth,

//   // UI State
//   showClosed,
//   setShowClosed,
//   sortBy,
//   setSortBy,
//   showFilterPanel,
//   setShowFilterPanel,
//   filterStatus,
//   setFilterStatus,
//   filterPriority,
//   setFilterPriority,
//   filterAssignee,
//   setFilterAssignee,
//   searchQuery,
//   setSearchQuery,
//   timePeriod,
//   handleTimePeriodChange,

//   // Team Members for Filter
//   teamMemberDetails,

//   // Handlers
//   handleResetFilters,
//   scrollToToday,
//   handleAutoFit,
//   exportPNG,
//   exportPDF,
//   exportExcel,
//   handleTaskChange,

//   // Stage Modal
//   showStageModal,
//   setShowStageModal,
//   editingStage,
//   setEditingStage,
//   projectId,
//   loadProjectStages,

//   // Task Edit Modal
//   showTaskEditModal,
//   setShowTaskEditModal,
//   editingTaskId,
//   setEditingTaskId,
//   loadProjectTasksByStages,

//   // Collapse State
//   collapsedStages,
//   setCollapsedStages,
// }) => {
//   return (
//     <div className="project-view-fade-in" style={{ margin: "10px" }}>
//       {/* Zoom + Export Controls */}
//       <div className="d-flex justify-content-between align-items-center mb-3">
//         {/* LEFT SIDE CONTROLS */}
//         <div className="d-flex gap-2 align-items-center">
//           {/* Closed / Active Toggle */}
//           <Button
//             size="sm"
//             variant={!showClosed ? "primary" : "outline-primary"}
//             onClick={() => setShowClosed(false)}
//           >
//             Active
//           </Button>

//           <Button
//             size="sm"
//             variant={showClosed ? "primary" : "outline-primary"}
//             onClick={() => setShowClosed(true)}
//           >
//             Closed
//           </Button>

//           {/* Sort Dropdown */}
//           <Form.Select
//             size="sm"
//             value={sortBy}
//             onChange={(e) => setSortBy(e.target.value)}
//             style={{ width: "160px" }}
//           >
//             <option value="stageStart">Stage Start Date</option>
//             <option value="stageEnd">Stage End Date</option>
//             <option value="taskStart">Task Start Date</option>
//             <option value="taskEnd">Task End Date</option>
//             <option value="status">Status</option>
//             <option value="priority">Priority</option>
//             <option value="assignee">Assignee</option>
//             <option value="duration">Duration</option>
//           </Form.Select>

//           {/* Filter Button */}
//           <Button
//             size="sm"
//             variant="outline-dark"
//             onClick={() => setShowFilterPanel(!showFilterPanel)}
//           >
//             Filter
//           </Button>

//           <Button
//             size="sm"
//             variant="outline-danger"
//             onClick={handleResetFilters}
//           >
//             Reset
//           </Button>
//         </div>

//         <div className="d-flex gap-2 align-items-center">
//           <Button size="sm" variant="outline-primary" onClick={scrollToToday}>
//             Today
//           </Button>

//           <Form.Select
//             size="sm"
//             value={timePeriod}
//             onChange={(e) => handleTimePeriodChange(e.target.value)}
//             style={{ width: "150px" }}
//           >
//             <option value="Day">Day</option>
//             <option value="Week">Week</option>
//             <option value="Month">Month</option>
//             <option value="Quarter">Quarter</option>
//             <option value="Year">Year</option>
//             <option value="Flexible">Flexible</option>
//           </Form.Select>

//           <Button size="sm" variant="outline-success" onClick={handleAutoFit}>
//             Auto Fit
//           </Button>

//           <Button size="sm" variant="outline-dark" onClick={exportPNG}>
//             Export PNG
//           </Button>

//           <Button size="sm" variant="outline-dark" onClick={exportPDF}>
//             Export PDF
//           </Button>

//           <Button size="sm" variant="outline-dark" onClick={exportExcel}>
//             Export Excel
//           </Button>
//         </div>

//         <Form.Control
//           type="text"
//           placeholder="Search stage or task..."
//           value={searchQuery}
//           onChange={(e) => setSearchQuery(e.target.value)}
//           style={{ width: "20vw", borderRadius: "20px" }}
//         />
//       </div>

//       {/* Filter Panel */}
//       {showFilterPanel && (
//         <Card className="mb-3 p-3 shadow-sm">
//           <Row>
//             <Col>
//               <Form.Label>Status</Form.Label>
//               <Form.Select
//                 multiple
//                 value={filterStatus}
//                 onChange={(e) =>
//                   setFilterStatus(
//                     Array.from(
//                       e.target.selectedOptions,
//                       (option) => option.value,
//                     ),
//                   )
//                 }
//               >
//                 <option>Not Started</option>
//                 <option>In Progress</option>
//                 <option>On Hold</option>
//                 <option>Completed</option>
//                 <option>Cancelled</option>
//               </Form.Select>
//             </Col>

//             <Col>
//               <Form.Label>Priority</Form.Label>
//               <Form.Select
//                 multiple
//                 value={filterPriority}
//                 onChange={(e) =>
//                   setFilterPriority(
//                     Array.from(
//                       e.target.selectedOptions,
//                       (option) => option.value,
//                     ),
//                   )
//                 }
//               >
//                 <option>High</option>
//                 <option>Medium</option>
//                 <option>Low</option>
//               </Form.Select>
//             </Col>

//             <Col>
//               <Form.Label>Assignee</Form.Label>
//               <Form.Select
//                 multiple
//                 value={filterAssignee}
//                 onChange={(e) =>
//                   setFilterAssignee(
//                     Array.from(
//                       e.target.selectedOptions,
//                       (option) => option.value,
//                     ),
//                   )
//                 }
//               >
//                 {teamMemberDetails.map((user) => (
//                   <option key={user.userId} value={String(user.userId)}>
//                     {user.username}
//                   </option>
//                 ))}
//               </Form.Select>
//             </Col>
//           </Row>
//         </Card>
//       )}

//       {/* Gantt Chart */}
//       <div id="gantt-container">
//         <Card className="shadow-sm border-0">
//           <Card.Body>
//             {finalGanttTasks.length === 0 ? (
//               <div className="text-center p-4">
//                 No valid tasks for Gantt view
//               </div>
//             ) : (
//               <Gantt
//                 tasks={finalGanttTasks}
//                 viewMode={viewMode}
//                 columnWidth={
//                   viewMode === ViewMode.Month
//                     ? 300
//                     : viewMode === ViewMode.Year
//                       ? 350
//                       : 65
//                 }
//                 listCellWidth="200px"
//                 onDateChange={handleTaskChange}
//                 onExpanderClick={(task) => {
//                   if (task.type !== "project") return;
//                   const stageId = task.id.replace("stage-", "");
//                   setCollapsedStages((prev) => ({
//                     ...prev,
//                     [stageId]: !prev[stageId],
//                   }));
//                 }}
//                 onDoubleClick={(task) => {
//                   if (!task) return;

//                   // If Stage → open Stage Modal
//                   if (task.type === "project") {
//                     const stageId = task.id.replace("stage-", "");
//                     const stageData = stages.find(
//                       (s) => String(s.stageId) === String(stageId),
//                     );
//                     if (stageData) {
//                       setEditingStage(stageData);
//                       setShowStageModal(true);
//                     }
//                     return;
//                   }

//                   // If Task → open Task Edit Modal
//                   setEditingTaskId(task.id);
//                   setShowTaskEditModal(true);
//                 }}
//                 todayColor="#ef4444"
//               />
//             )}

//             {/* Stage Modal */}
//             <StageModal
//               show={showStageModal}
//               onHide={() => {
//                 setShowStageModal(false);
//                 setEditingStage(null);
//               }}
//               stage={editingStage}
//               projectId={projectId}
//               onStageSaved={() => {
//                 loadProjectStages();
//               }}
//             />

//             {/* Task Edit Modal */}
//             <TaskEditModal
//               show={showTaskEditModal}
//               onHide={() => {
//                 setShowTaskEditModal(false);
//                 setEditingTaskId(null);
//               }}
//               taskId={editingTaskId}
//               onTaskUpdated={() => {
//                 loadProjectTasksByStages();
//               }}
//             />
//           </Card.Body>
//         </Card>
//       </div>
//     </div>
//   );
// };

// export default ProjectGanttTab;
